<template>
  <div 
    class="text-block" 
    :style="containerStyle"
    :id="block.blockAnchorID || undefined"
  >
    <div :class="getContainerClasses('block-container')">
      <IO v-if="block.title">
        <h2 v-if="block.title" class="text-block__title">{{ block.title }}</h2>
      </IO>
      
      <div v-if="block.body" class="text-block__body">
        <RichTextRenderer :nodes="renderedContent" />
      </div>

      <ButtonBlock v-if="block.button" :block="block.button" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BlockProps } from './index'
import { useRichText } from '../../composables/useRichText'
import IO from '../IO.vue'
import ButtonBlock from './ButtonBlock.vue'
import RichTextRenderer from '../RichTextRenderer.vue'
import { useContainerSize } from '../../composables/useContainerSize'
import { computed } from 'vue'

const props = defineProps<BlockProps>()
const { renderRichText } = useRichText()

const { containerClass, containerStyle, getContainerClasses } = useContainerSize(props.block.size || { size: 'Default' })

const renderedContent = computed(() => {
  // Try different property names that Strapi might use
  const content = props.block.body || props.block.Content || props.block.text || null

  //console.log('props.block.blockAnchorID',props.block);
  
  // Debug log to verify content is found
  if (!content) {
    console.log('No rich text content found in TextBlock:', props.block)
  }
  
  return content ? renderRichText(content) : []
})
</script>

<style lang="scss" scoped>
@use '../../assets/scss/variables' as *;
@use '../../assets/scss/container' as *;
@use '../../assets/scss/typography' as *;
@use '../../assets/scss/mixins' as *;

.text-block {
  height: var(--container-height);
  
  @include below-tablet-landscape {
    height: var(--container-height-mobile);
  }
}

.block-container {
  width: 100%;
  // Add padding for mobile when needed
  @include below-tablet-landscape {
    padding: 0 $spacing-md;
  }
}

.text-block {
  &__title {
    margin-bottom: $spacing-lg;
  }

  &__body {
   
   // max-width: 950px;
    :deep(p) {
      margin-bottom: $spacing-md;
    }

    :deep(h1, h2, h3, h4, h5, h6) {
      margin: $spacing-lg 0 $spacing-md;
    }

    :deep(ul, ol) {
      margin: $spacing-md 0;
      padding-left: $spacing-xl;
    }

    :deep(li) {
      margin-bottom: $spacing-sm;
    }

    :deep(a) {
      color: var(--theme-accent);
      text-decoration: underline;
      
      &:hover {
        text-decoration: none;
      }
    }

    :deep(blockquote) {
      margin: $spacing-lg 0;
      padding: $spacing-md $spacing-lg;
      border-left: 4px solid var(--theme-accent);
      background-color: var(--theme-background-alt, #f5f5f5);
      font-style: italic;
    }

    :deep(.code-block) {
      margin: $spacing-lg 0;
      padding: $spacing-md;
      background-color: var(--theme-code-background, #1e1e1e);
      color: var(--theme-code-text, #ffffff);
      border-radius: 4px;
      overflow-x: auto;
      font-family: 'Courier New', Courier, monospace;
      font-size: 0.9em;
      line-height: 1.4;
    }

    :deep(.inline-code) {
      padding: 2px 6px;
      background-color: var(--theme-code-background-inline, #f1f1f1);
      color: var(--theme-code-text-inline, #333);
      border-radius: 3px;
      font-family: 'Courier New', Courier, monospace;
      font-size: 0.9em;
    }

    // Images in rich text should have reasonable sizing
    :deep(.responsive-image) {
      max-width: 100%;
      height: auto;
      margin: $spacing-lg 0;
    }
  }
}
</style> 